import React from "react";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { IconDownloadBlueSvg } from "../../common/utils/icons";
import {
  extractFileAndDirectoriesName,
  handleSecureDownload,
} from "../../services/utils";
import { GridCloseIcon } from "@mui/x-data-grid";

interface ErrorPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  errorData: {
    job_status: string;
    exception_message: string;
    log_file: string | null;
  } | null;
  isLoading?: boolean;
  title?: string;
}

const ErrorPopover: React.FC<ErrorPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  errorData,
  isLoading = false,
  title = "Error Details",
}) => {
  const getLogFilePath = (path: any) => {
    const parts = extractFileAndDirectoriesName(path);
    let filepath = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
    return { filepath, fileName: parts[parts.length - 1] };
  };
  const downloadLogFile = (logFile: any) => {
    if (logFile === null || logFile === undefined) {
      return;
    }
    const { filepath, fileName }: any = getLogFilePath(logFile);
    handleSecureDownload(filepath, fileName);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          className: "error-popover",
        },
      }}
    >
      <Box sx={{ width: 400, maxHeight: 300 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 16px",
            borderBottom: "1px solid #eee",
          }}
        >
          <Box className="title">{title}</Box>
          <Box className="icon-close" onClick={onClose}>
            <GridCloseIcon />
          </Box>
        </Box>

        <List className="error-details-list">
          {isLoading ? (
            <ListItem>
              <ListItemText primary="Loading error details..." />
            </ListItem>
          ) : errorData ? (
            <ListItem className="bg">
              <ListItemText
                primary={<></>}
                secondary={
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Error Message:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "error.dark",
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        mb: errorData.log_file ? 2 : 0,
                      }}
                    >
                      {errorData.exception_message}
                    </Typography>

                    {errorData.log_file && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Log File Available
                        </Typography>
                        <Tooltip
                          title="Download Log File"
                          placement="top"
                          arrow
                        >
                          <Box
                            onClick={() => downloadLogFile(errorData.log_file!)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                              },
                              "& svg": {
                                width: "20px",
                                height: "20px",
                                fill: "#1976d2",
                              },
                            }}
                          >
                            <IconDownloadBlueSvg />
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                }
                secondaryTypographyProps={{
                  sx: {
                    overflow: "visible",
                  },
                }}
              />
            </ListItem>
          ) : (
            <ListItem>
              <ListItemText primary="No error data available" />
            </ListItem>
          )}
        </List>
      </Box>
    </Popover>
  );
};

export default ErrorPopover;
